 
import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { User2Icon, LogOut, Lightbulb, MessageCircleQuestionMark, Sun, Moon } from 'lucide-react';
import LogoDark from '../../assets/logo/Logo.png';
import LogoLight from '../../assets/logo/logo-dark.png';
import useTheme from '../../hooks/useTheme';

const DesktopNavigation = ({
  navigation,
  isCurrentPath,
  identity,
  onLogout,
  onStartTour,
  contactSupportUrl,
}) => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <aside className='hidden lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex lg:flex-col sidebar z-30'>
      {/* Logo */}
      <div className='h-16 flex items-center px-5 shrink-0' style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
        <img src={isDark ? LogoDark : LogoLight} alt='Postreams Logo' className='h-9 w-auto' />
      </div>

      {/* Nav links */}
      <nav className='flex-1 px-3 py-5 space-y-0.5 overflow-y-auto thin-scrollbar' data-tour='nav-links'>
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive = isCurrentPath(item.href);
          const navLabel = item.label || item.name;

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`desktop-tour-${item.name
                .replace(/\s+/g, '-')
                .toLowerCase()} sidebar-link relative ${isActive ? 'active' : ''}`}
              onClick={(e) => {
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                }
              }}
            >
              <Icon className='sidebar-icon h-4.5 w-4.5' style={{ width: '1.125rem', height: '1.125rem' }} />
              <span>{navLabel}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className='p-3 space-y-1' style={{ borderTop: '1px solid var(--sidebar-border)' }}>
        {/* Utility buttons */}
        <button
          type='button'
          onClick={onStartTour}
          className='sidebar-link w-full'
          style={{ border: 'none', background: 'none' }}
        >
          <Lightbulb style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
          <span>Start Tour</span>
        </button>

        <button
          type='button'
          onClick={() => (window.location.href = contactSupportUrl)}
          className='sidebar-link w-full'
          style={{ border: 'none', background: 'none' }}
        >
          <MessageCircleQuestionMark style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
          <span>Contact Support</span>
        </button>

        {/* Theme toggle */}
        <button
          type='button'
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className='sidebar-link w-full'
          style={{ border: 'none', background: 'none' }}
        >
          {isDark ? (
            <Sun style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
          ) : (
            <Moon style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
          )}
          <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
        </button>

        {/* User info */}
        <div
          className='flex items-center gap-3 px-3 py-2.5 mt-1 rounded-xl'
          style={{ background: 'var(--bg-surface)' }}
        >
          <div
            className='w-8 h-8 rounded-full flex items-center justify-center shrink-0'
            style={{ background: 'var(--bg-card-alt)', border: '1px solid var(--border)' }}
          >
            <User2Icon style={{ width: '1rem', height: '1rem', color: 'var(--text-muted)' }} />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium truncate' style={{ color: 'var(--text-primary)' }}>
              {identity?.full_name || identity?.email || 'User'}
            </p>
            <p className='text-xs' style={{ color: 'var(--text-ghost)' }}>
              {format(new Date(), 'MMM do yyyy', { locale: enUS })}
            </p>
          </div>
        </div>

        {/* Logout */}
        <button
          type='button'
          onClick={onLogout}
          className='sidebar-link w-full mt-1'
          style={{ border: 'none', background: 'none', color: 'var(--text-muted)' }}
        >
          <LogOut style={{ width: '1.125rem', height: '1.125rem', color: 'var(--sidebar-icon)' }} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default DesktopNavigation;
